import type { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import { NextSeo } from "next-seo";

import Layout from "../../components/layout/LayoutWrapper";
import styled, { css } from "styled-components";
import { productById } from "library/services/shopify/product";
import { TypeBodyM, Wrapper } from "../../styles/mixins";
import ProductLanding from "../../components/product/ProductLanding";
import ProductPacking from "../../components/product/ProductPacking";
import ProductCardList from "../../components/product/card/ProductCardList";
import { useSettings } from "../../contexts/SettingsProvider";
import {
  Breakpoint,
  Columns,
  MobileBreakpoint,
  TabletBreakpoint,
} from "../../styles/functions";
import SanityLuxury from "../../components/sanity/SanityLuxury";
import ProductProvider from "../../contexts/ProductProvider";
import {
  fetchProductPage,
  fetchProductPaths,
  routeFetcher,
} from "library/services/fetchers";
import { formatSanityProductSeo } from "../../utils/seo";
import ProductLandingMobile from "../../components/product/ProductLandingMobile";
import OverlayMobileControls from "../../components/overlay/OverlayMobileControls";
import useBreakpoints from "../../hooks/useBreakpoints";
import { calculateObjectSize } from "library/utils/stats";
import ProductSchema from "../../components/product/ProductSchema";
import { klaviyoViewProduct } from "../../hooks/useKlaviyo";
import { SHOPIFY_DEFAULT_LOCATION } from "library/utils/shopify";
import { pixelViewProduct } from "../../hooks/usePixel";
import { useEffect } from "react";
import { isServer } from "../../utils/env";
import { useQuery } from "@tanstack/react-query";
import { useCart } from "../../contexts/CartProvider";
import { gaViewProduct } from "../../hooks/useGA";
import PageLoading from "../../components/page/PageLoading";
import { staticPropsError } from "../../utils/error";
import { createHashKey } from "library/utils";
import { isProduction } from "../../hooks/useFlags";

export const getStaticPaths = fetchProductPaths;

export async function getStaticProps({
  params,
  preview = false,
  locale = SHOPIFY_DEFAULT_LOCATION,
}: GetStaticPropsContext<{
  handle: string;
}>) {
  const handle = params?.handle;
  try {
    const key = createHashKey("productPage", { locale, preview, handle });
    const data = await routeFetcher({
      key,
      fetcher: () => fetchProductPage({ locale, preview, handle }),
      preview,
    });

    const { kiloBytes } = calculateObjectSize(data);
    console.log(`Page ${handle} size: ${kiloBytes}kb`);

    return {
      revalidate: isProduction ? undefined : 1,
      props: { data },
    };
  } catch (e: any) {
    return staticPropsError(e);
  }
}

export default function Product({
  data,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { cart } = useCart();
  const product = data?.product;
  const locale = data?.locale;
  const propsShopifyProduct = data?.shopifyProduct;

  // Limit to 2 related products on mobile
  const { breakpoint } = useBreakpoints();
  const relatedProducts =
    product?.related?.length && breakpoint == "mobile"
      ? (product?.related || []).slice(0, 2)
      : product?.related;

  if (!product || data?.error) return <PageLoading />;

  const settings = useSettings();

  // Refresh product data on load
  const { data: shopifyProduct, refetch } = useQuery(
    ["products", propsShopifyProduct.id],
    async () => {
      const product = await productById(propsShopifyProduct.id, {
        countryCode: locale?.toUpperCase() || SHOPIFY_DEFAULT_LOCATION,
      });
      return product;
    },
    { initialData: propsShopifyProduct },
  );

  useEffect(() => {
    refetch();
  }, [locale]);

  // Product viewed trackers
  useEffect(() => {
    if (isServer() || !cart) return;
    klaviyoViewProduct({ locale, shopifyProduct });
    pixelViewProduct(cart, { shopifyProduct });
    gaViewProduct(shopifyProduct);
  }, [cart]);

  return (
    <>
      <NextSeo
        {...formatSanityProductSeo(
          `/products/${data.handle}`,
          {
            ...product,
            shopifyProduct,
          },
          locale.toUpperCase(),
        )}
      />

      <ProductSchema locale={locale} shopifyProduct={shopifyProduct} />

      <ProductProvider product={product} shopifyProduct={shopifyProduct}>
        <Container>
          <Landing />
          <LandingMobile />

          {product.packing && <ProductPacking />}

          {product.related?.length && (
            <Related>
              <ProductsHeading>Related Products</ProductsHeading>
              <Products layout="S" products={relatedProducts} />
            </Related>
          )}

          {product.withCare && settings.care?.products?.length && (
            <Care>
              <ProductsHeading>Care Products</ProductsHeading>
              <CareProducts
                layout="S"
                products={settings.care?.products || []}
              />
            </Care>
          )}
        </Container>
        {product.withLuxury && <SanityLuxury />}
        <OverlayMobileControls />
      </ProductProvider>
    </>
  );
}

const Container = styled.div`
  ${Wrapper};
  /* margin-top: var(--spacing-page-top); */
`;

const Landing = styled(ProductLanding)`
  ${MobileBreakpoint} {
    display: none;
  }
`;

const LandingMobile = styled(ProductLandingMobile)`
  ${Breakpoint("mobile", "min")} {
    display: none;
  }
`;

const ProductsHeading = styled.h2`
  ${TypeBodyM};
  margin-bottom: var(--spacing-24);
`;

const Products = styled(ProductCardList)`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--spacing-24) var(--gutter);
  margin-bottom: var(--spacing-120);

  ${TabletBreakpoint(css`
    grid-template-columns: repeat(4, 1fr);
  `)}

  ${MobileBreakpoint} {
    width: ${Columns(10)};
    grid-template-columns: 1fr 1fr;
  }
`;

const Care = styled.div`
  margin-left: auto;
  width: ${Columns(8)};

  ${TabletBreakpoint(`
    width: ${Columns(12)};
    /* margin-left:0; */
  `)}

  ${MobileBreakpoint} {
    width: ${Columns(10)};
    margin-left: 0;
  }
`;

const Related = styled.div`
  padding-top: var(--spacing-24);
`;

const CareProducts = styled(Products)`
  grid-template-columns: 1fr 1fr;
`;

Product.Layout = Layout;
